import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";

// Portals an absolutely-positioned panel anchored under `anchorRef` to <body>,
// so dropdowns escape any scrollable parent (e.g. the modal body) and never push
// content height. Position recomputes on scroll/resize while open.
const PortalPopover = ({ open, anchorRef, onClose, children, maxHeight = 260 }) => {
    const [coords, setCoords] = useState(null);
    const panelRef = useRef(null);
    useLayoutEffect(() => {
        if (!open || !anchorRef.current) return;
        const update = () => {
            const r = anchorRef.current?.getBoundingClientRect();
            if (r) setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
        };
        update();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [open, anchorRef]);
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (anchorRef.current?.contains(e.target)) return;
            if (panelRef.current?.contains(e.target)) return;
            onClose?.();
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open, anchorRef, onClose]);
    if (!open || !coords) return null;
    return createPortal(
        <div
            ref={panelRef}
            style={{ position: "fixed", top: coords.top, left: coords.left, width: coords.width, maxHeight, zIndex: 1100 }}
            className="flex flex-col bg-cm-p-surface rounded-lg border border-cm-p-outline shadow-lg overflow-hidden"
        >
            {children}
        </div>,
        document.body,
    );
};

const Checkbox = ({ checked }) => (
    <span
        className={`w-4 h-4 rounded border-2 flex items-center justify-center border-cm-p-accent-500 shrink-0 ${
            checked ? "bg-cm-p-accent-500" : ""
        }`}
    >
        {checked && <Check className="size-3 text-cm-p-surface" />}
    </span>
);

// Searchable multi-select dropdown with checkboxes and a "select all" option.
// `value` is an array of selected option ids; `onChange` receives the next array.
export const MultiSelectField = ({ value, onChange, options, allLabel = "All", searchPlaceholder = "Search" }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const buttonRef = useRef(null);

    const selected = useMemo(() => new Set(value ?? []), [value]);
    const filtered = useMemo(() => {
        if (!search) return options;
        const q = search.toLowerCase();
        return options.filter((o) => (o.text || "").toLowerCase().includes(q));
    }, [options, search]);

    const display = (() => {
        if (selected.size === 0) return "None";
        if (selected.size >= options.length) return `All ${allLabel}`;
        return `${selected.size} selected`;
    })();

    const allFilteredChecked = filtered.length > 0 && filtered.every((o) => selected.has(o.id));
    const toggleAll = () => {
        const next = new Set(selected);
        if (allFilteredChecked) filtered.forEach((o) => next.delete(o.id));
        else filtered.forEach((o) => next.add(o.id));
        onChange(Array.from(next));
    };
    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        onChange(Array.from(next));
    };
    const handleClose = () => {
        setOpen(false);
        setSearch("");
    };

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`w-full flex items-center justify-between bg-cm-p-surface-container rounded-[6px] px-3 h-[42px] border ${
                    open ? "border-cm-p-accent-200" : "border-cm-p-outline"
                } text-[14px] font-medium text-cm-p-text-primary`}
            >
                <span>{display}</span>
                <ChevronDown className={`w-4 h-4 text-cm-p-text-secondary shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            <PortalPopover open={open} anchorRef={buttonRef} onClose={handleClose} maxHeight={340}>
                <div className="px-2 pt-2 pb-2 border-b border-cm-p-outline shrink-0">
                    <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-cm-p-text-secondary" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="w-full h-[34px] pl-8 pr-2 text-[13px] bg-cm-p-surface-container border border-cm-p-outline rounded-[6px] text-cm-p-text-primary placeholder:text-cm-p-text-secondary focus:outline-none focus:border-cm-p-accent-200"
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto py-1">
                    {filtered.length > 0 && (
                        <button
                            type="button"
                            onClick={toggleAll}
                            className="w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] font-medium text-cm-p-text-primary hover:bg-cm-p-surface-hover"
                        >
                            <Checkbox checked={allFilteredChecked} />
                            <span>{search ? "Select all (filtered)" : "Select all"}</span>
                        </button>
                    )}
                    {filtered.map((opt) => (
                        <button
                            type="button"
                            key={opt.id}
                            onClick={() => toggle(opt.id)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] font-medium text-cm-p-text-primary hover:bg-cm-p-surface-hover"
                        >
                            <Checkbox checked={selected.has(opt.id)} />
                            <span>{opt.text}</span>
                        </button>
                    ))}
                    {filtered.length === 0 && <div className="px-3 py-3 text-[13px] text-cm-p-text-secondary text-center">No results</div>}
                </div>
            </PortalPopover>
        </>
    );
};
